<template>
  <div class="account-info">
    <!-- Plan Features Grid -->
    <div class="features-grid">
      <!-- Components -->
      <div class="feature-card">
        <div class="feature-icon components">
          <b-icon icon="box-seam"></b-icon>
        </div>
        <div class="feature-content">
          <span class="feature-label">{{ $t('accountInfo.components') }}</span>
          <span class="feature-value">{{ account.components }}</span>
        </div>
      </div>

      <!-- Forge -->
      <div class="feature-card">
        <div class="feature-icon forge">
          <b-icon icon="gear-wide-connected"></b-icon>
        </div>
        <div class="feature-content">
          <span class="feature-label">{{ $t('accountInfo.forge') }}</span>
          <span class="feature-value">
            <span v-if="account.forge < 99999">{{ account.forge }}</span>
            <span v-else class="unlimited">∞ {{ $t('accountInfo.unlimited') }}</span>
          </span>
        </div>
      </div>

      <!-- Rate Limit -->
      <div class="feature-card">
        <div class="feature-icon rate-limit">
          <b-icon icon="speedometer2"></b-icon>
        </div>
        <div class="feature-content">
          <span class="feature-label">{{ $t('accountInfo.rateLimit') }}</span>
          <span class="feature-value">{{ formatNumber(account.rate_limit) }}/{{ $t('accountInfo.min') }}</span>
        </div>
      </div>

      <!-- Max Concurrent Requests -->
      <div class="feature-card">
        <div class="feature-icon concurrent">
          <b-icon icon="layers"></b-icon>
        </div>
        <div class="feature-content">
          <span class="feature-label">{{ $t('accountInfo.maxConcurrent') }}</span>
          <span class="feature-value">{{ account.max_concurrent_requests }}</span>
        </div>
      </div>
    </div>

    <!-- Pay As You Go Section -->
    <div class="payg-section" v-if="prices && account.pay_as_you_go && !account.flat_rate">
      <div class="payg-header">
        <div class="payg-title">
          <b-icon icon="credit-card" class="mr-2"></b-icon>
          {{ $t('accountInfo.payAsYouGo') }}
        </div>
        <b-badge variant="info" pill>{{ $t('billedMonthly') }}</b-badge>
      </div>
      
      <div class="payg-grid">
        <!-- Component Pricing Group -->
        <div class="payg-group" v-if="prices.component">
          <div class="payg-group-header">
            <b-icon icon="box-seam" class="mr-2"></b-icon>
            {{ $t('accountInfo.componentPricing') }}
          </div>
          <div class="payg-group-items">
            <div class="payg-subitem">
              <span class="payg-label">{{ $t('accountInfo.componentDocsExamined') }}</span>
              <span class="payg-price">{{ formatUnitPrice(prices.component.TotalDocsExaminedPrice, prices.component.Precision, 'doc') }}</span>
            </div>
            <div class="payg-subitem">
              <span class="payg-label">{{ $t('accountInfo.componentBytesReturned') }}</span>
              <span class="payg-price">{{ formatUnitPrice(prices.component.TotalBytesReturnedPrice, prices.component.Precision, 'byte') }}</span>
            </div>
          </div>
        </div>

        <!-- Forge Pricing -->
        <div class="payg-item" v-if="prices.forge">
          <span class="payg-label">
            <img src="/img/developer/forge.png" alt="Forge" style="width: 20px; height: 20px; margin-right: 0.5rem; vertical-align: middle;">
            {{ $t('accountInfo.forgeApps') }}
            <small v-if="account.base_forge > 0">({{ $t('accountInfo.afterBase', { count: account.base_forge }) }})</small>
          </span>
          <span class="payg-price">{{ formatUnitPrice(prices.forge.Price, prices.forge.Precision, 'app') }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
$dark-background: var(--bg-card);
$dark-hard-background: var(--form-bg);
$dark-border: var(--border-hover);
$dark-hard2-background: var(--bg-card);
$dark-border2: var(--border-default);
$protokol-royal-active: var(--primary-start);

.account-info {
  padding: 0.5rem 0;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
}

.feature-card {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem;
  background: $dark-hard2-background;
  border: 1px solid $dark-border2;
  border-radius: 10px;
  transition: all 0.2s ease;

  &:hover {
    border-color: $dark-border;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  }
}

.feature-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  flex-shrink: 0;

  svg {
    width: 20px;
    height: 20px;
    color: var(--text-primary);
  }

  &.support { background: linear-gradient(135deg, #6c80ff 0%, #764ba2 100%); }
  &.components { background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%); }
  &.records { background: linear-gradient(135deg, #ee9ca7 0%, #ffdde1 100%); 
    svg { color: #c44569; }
  }
  &.public-apps { background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%); }
  &.platform-apps { background: linear-gradient(135deg, #fa709a 0%, #fee140 100%); }
  &.workspaces { background: linear-gradient(135deg, #5ee7df 0%, #b490ca 100%); }
  &.event-bus { background: linear-gradient(135deg, #d299c2 0%, #fef9d7 100%);
    svg { color: #8e44ad; }
  }
  &.forge { background: linear-gradient(135deg, #f7971e 0%, #ffd200 100%); }
  &.rate-limit { background: linear-gradient(135deg, #00c6ff 0%, #0072ff 100%); }
  &.concurrent { background: linear-gradient(135deg, #834d9b 0%, #d04ed6 100%); }
  &.build-minutes { background: linear-gradient(135deg, #ff6b6b 0%, #feca57 100%); }
  &.memory { background: linear-gradient(135deg, #48dbfb 0%, #0abde3 100%); }
}

.feature-content {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.feature-label {
  font-size: 0.75rem;
  color: #adb5bd;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 2px;
}

.feature-value {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 0.5rem;

  .unlimited {
    color: #48bb78;
  }
}

.info-icon {
  color: #ffffff;
  cursor: help;
  font-size: 0.875rem;
}

/* Pay As You Go Section */
.payg-section {
  margin-top: 1.5rem;
  padding: 1.25rem;
  background: $dark-hard-background;
  border: 1px solid $dark-border2;
  border-radius: 12px;
}

.payg-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid $dark-border2;
}

.payg-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
}

.payg-grid {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.payg-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px dashed $dark-border2;

  &:last-child {
    border-bottom: none;
  }
}

.payg-group {
  background: $dark-hard2-background;
  border-radius: 8px;
  padding: 0.75rem;
  margin-bottom: 0.5rem;
}

.payg-group-header {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  padding-bottom: 0.5rem;
  margin-bottom: 0.5rem;
  border-bottom: 1px solid $dark-border2;
}

.payg-group-items {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.payg-subitem {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.375rem 0 0.375rem 1.5rem;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 0.5rem;
    top: 50%;
    transform: translateY(-50%);
    width: 4px;
    height: 4px;
    background: $dark-border;
    border-radius: 50%;
  }
}

.payg-label {
  font-size: 0.875rem;
  color: #adb5bd;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  small {
    color: #6c757d;
    font-weight: 500;
  }
}

.payg-price {
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--text-primary);
  white-space: nowrap;
}
</style>

<script>
export default {
  props: {
    account: {
      type: Object,
      required: true
    },
    prices: {
      type: Object
    },
    currency: {
      type: String
    }
  },

  methods: {
    formatNumber(num) {
      if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + 'M'
      } else if (num >= 1000) {
        return (num / 1000).toFixed(0) + 'K'
      }
      return num.toLocaleString()
    },

    formatMemorySeconds(mbSeconds) {
      // Convert MB-seconds to GB-seconds for better readability
      if (mbSeconds >= 1000000) {
        return `${(mbSeconds / 1000000).toFixed(1)}M GB-sec`
      } else if (mbSeconds >= 1000) {
        return `${(mbSeconds / 1000).toFixed(0)}K GB-sec`
      }
      return `${(mbSeconds / 1024).toFixed(1)} GB-sec`
    },

    formatWithPrecision(price, precision) {
      if (!precision || precision === 0) return price
      const result = price / precision
      // Remove trailing zeros by converting to number and back to string
      return parseFloat(result.toPrecision(8)).toString()
    },

    formatUnitPrice(value, precision, unit) {
      if (!value || !precision) return '-'
      
      const symbols = {
        USD: '$',
        EUR: '€',
        RSD: 'RSD',
      }
      const symbol = symbols[this.currency] || this.currency || '€'
      
      let pricePerUnit = value / precision
      let displayUnit = unit || 'unit'
      let multiplier = 1
      
      // Convert bytes to GB (1 GB = 1,000,000,000 bytes)
      if (unit === 'byte' || unit === 'bytes') {
        const bytesInGB = 1000000000
        pricePerUnit = pricePerUnit * bytesInGB
        displayUnit = 'GB'
      }
      // Convert protokol sandbox mb to GB (1 GB = 1,000 MB)
      else if (unit === 'protokol sandbox mb' || unit === 'mb' || unit === 'MB') {
        const mbInGB = 1000
        pricePerUnit = pricePerUnit * mbInGB
        displayUnit = 'GB'
      }
      // Convert milliseconds to minutes
      else if (unit === 'ms' || unit === 'millisecond' || unit === 'milliseconds') {
        const msInMinute = 60 * 1000
        pricePerUnit = pricePerUnit * msInMinute
        displayUnit = 'minute'
      }
      
      // If price is still very small, find a better multiplier to show
      if (pricePerUnit < 0.01 && pricePerUnit > 0) {
        // Calculate how many units we need to get to at least $1
        const baseMultiplier = Math.ceil(1 / pricePerUnit)
        
        // Round to nice numbers
        if (baseMultiplier < 100) {
          multiplier = Math.ceil(baseMultiplier / 10) * 10
        } else if (baseMultiplier < 1000) {
          multiplier = Math.ceil(baseMultiplier / 100) * 100
        } else if (baseMultiplier < 10000) {
          multiplier = Math.ceil(baseMultiplier / 1000) * 1000
        } else if (baseMultiplier < 100000) {
          multiplier = Math.ceil(baseMultiplier / 10000) * 10000
        } else if (baseMultiplier < 1000000) {
          multiplier = Math.ceil(baseMultiplier / 100000) * 100000
        } else {
          multiplier = Math.ceil(baseMultiplier / 1000000) * 1000000
        }
        
        const adjustedPrice = pricePerUnit * multiplier
        const pluralUnit = multiplier > 1 && displayUnit === 'minute' ? 's' : ''
        return `${symbol}${adjustedPrice.toFixed(2)} / ${multiplier.toLocaleString()} ${displayUnit}${pluralUnit}`
      }
      
      // For normal prices, show per 1 unit
      return `${symbol}${pricePerUnit.toFixed(2)} / 1 ${displayUnit}`
    },

    formatPrice(cents) {
      return (cents / 100).toFixed(2)
    }
  }
}
</script>